import React, { useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import Image from 'next/image'
import parse from 'html-react-parser'
import { FormProps } from '~types'
import { fireAnalyticsEvent } from '~analyticsUtils'
import { Button, Heading, PortalModal } from '~elements'
import { NEXT_IMAGE_QUALITY } from '~constants'

const ReactFormstack = dynamic(() => import('@msgtechnology/react-formstack').then(module => module.default), {
  ssr: false
})

interface FormstackData {
  id: string
}

const Form = ({
  formId,
  isChildOfModal = false,
  submitButtonBgColor = 'white',
  submitButtonText = 'Sign Up Now',
  showSuccessMessageInModal = false,
  onCloseModalHandler,
  formSuccessLogo,
  formSubmitHandler,
  modalSuccessMessage,
  modalSuccessTitle
}: FormProps) => {
  const { asPath } = useRouter()
  const [isSuccessful, setIsSuccessful] = useState<boolean>(false)

  const formStackApiUrl = process.env.NEXT_PUBLIC_FORMSTACK_API_URL

  if (!formStackApiUrl || !formId) return null

  const afterSubmit = (data: FormstackData) => {
    if (data.id != undefined) {
      setIsSuccessful(true)
      // No GTM event, analytics collected from Formstack
    }
  }

  const formClassNames = `formstack formstack--input-bg-color-transparent formstack--submit-btn-bg-${submitButtonBgColor}`

  return (
    <>
      {isSuccessful && !showSuccessMessageInModal ? (
        <div className={`formstack-success ${isChildOfModal ? 'is-child-of-modal' : 'is-child-of-page-content'}`}>
          <Heading level={3}>Success!</Heading>
          <p>We've received your request.</p>
          {isChildOfModal && <Button color='light' fill='hollow' size='md' onClick={formSubmitHandler} title='Close' />}
        </div>
      ) : (
        <ReactFormstack
          className={formClassNames}
          id={formId.toString()}
          onAfterSubmit={afterSubmit}
          submitButtonText={submitButtonText}
          endpoint={formStackApiUrl}
        />
      )}

      <PortalModal
        isOpen={isSuccessful && showSuccessMessageInModal}
        bgColor='form-result'
        {...(onCloseModalHandler && {
          handleModalClose: onCloseModalHandler
        })}
        closeOnOutsideClick={true}
      >
        <div className='form-modal-wrapper'>
          {formSuccessLogo && formSuccessLogo?.url && (
            <Image
              width={54}
              height={54}
              alt='Form success icon'
              sizes='100vw'
              src={formSuccessLogo.url}
              quality={NEXT_IMAGE_QUALITY}
            />
          )}
          {modalSuccessTitle && <Heading level={4}>{modalSuccessTitle}</Heading>}
          {modalSuccessMessage && modalSuccessMessage?.html && (
            <Heading level={4}>{parse(modalSuccessMessage.html)}</Heading>
          )}
        </div>
      </PortalModal>
    </>
  )
}

export default Form
